import React, { useEffect } from 'react';

import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import Button, { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import SurveyIcon from '@atlaskit/icon-lab/core/survey';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { getSurveyCreationFlagContent } from '@atlassian/conversation-assistant-message-actions-api/survey-api-error';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { ResolveActionFnc } from '../../../common/types';
import { SystemMessage } from '../../../common/ui/system-message';

import type { JsmCreateSurveyCardQuery } from './__generated__/JsmCreateSurveyCardQuery.graphql';
import { JsmCreateSurveyFooter as Footer } from './JsmCreateSurveyFooter';
import { JsmCreateSurveyMainContent as MainContent } from './JsmCreateSurveyMainContent';
import { JsmCreateSurveyPreviewContent as PreviewContent } from './JsmCreateSurveyPreviewContent';
import { useJsmCreateSurvey } from './useJsmCreateSurvey';

const surveyPreviewCardTitle = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-title',
	defaultMessage: 'Create survey',
	description: 'The title of the survey preview card',
});

const surveyPreviewCardPreviewButton = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-preview-button',
	defaultMessage: 'Preview',
	description: 'The preview action button label in the survey preview card footer',
});

const surveyPreviewCardCreateButton = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-create-button',
	defaultMessage: 'Create',
	description: 'The create action button label in the survey preview card footer',
});

const surveyPreviewCardCreatingButton = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-creating-button',
	defaultMessage: 'Creating…',
	description: 'The button label shown while a survey is being created',
});

const surveyPreviewCardOpenButton = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-open-button',
	defaultMessage: 'Open',
	description: 'The open action button label in the survey preview card footer',
});

const surveyPreviewCardDismissButton = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-dismiss-button',
	defaultMessage: 'Dismiss',
	description: 'The dismiss action button label in the survey preview card footer',
});

const surveyPreviewCardDismissedMessage = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-dismissed-message',
	defaultMessage: "You've dismissed the suggested survey",
	description: 'System message displayed when the suggested survey is dismissed',
});

const surveyPreviewCardActionMessage = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-action-message',
	defaultMessage:
		"I\'ve drafted a survey for you to review: <title></title>. Select <bold>Create</bold> when you're ready. It will be added to the current space.",
	description:
		'Message text displayed above the survey preview card in the AI conversation interface when the AI suggests creating a new JSM survey. The <bold> tags will be rendered as bold text for "Create". The purpose is to present the AI-generated survey to the user for review and provide clear instructions on how to proceed with creating it.',
});

const surveyPreviewCardSuccessMessage = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-success-message',
	defaultMessage: 'You created {title}',
	description:
		'Success message shown in the pill after a survey is successfully created via AI. {title} is the name of the newly created survey.',
});

const surveyPreviewCardToggleCloseLabel = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-toggle-close-label',
	defaultMessage: 'Close',
	description:
		'Accessibility label for the chevron toggle button on the survey preview card when the card is expanded and can be collapsed.',
});

const surveyPreviewCardToggleOpenLabel = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-toggle-open-label',
	defaultMessage: 'Open',
	description:
		'Accessibility label for the chevron toggle button on the survey preview card when the card is collapsed and can be expanded.',
});

const styles = cssMap({
	container: {
		width: '100%',
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		boxShadow: token('elevation.shadow.raised'),
	},
	header: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'left',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	content: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
	icon: {
		width: '32px',
		height: '32px',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.tile'),
		borderColor: token('color.border'),
	},
});

export type Props = {
	actions: MessageAction[];
	resolveAction?: ResolveActionFnc;
	isSelected: boolean;
	isEnabled?: boolean;
	stagingAreaEnabled?: boolean;
	cloudId: string;
	onPreview: (e: React.MouseEvent<HTMLButtonElement>) => void;
	conversationId?: string;
};

export const JsmCreateSurveyCard = (props: Props): React.JSX.Element => {
	const { actions, cloudId, onPreview } = props;
	const [firstAction] = actions;
	const projectIdOrKey = String((firstAction?.data as Record<string, unknown>)?.projectId ?? '');
	const { sendAnalyticsEvent } = useAnalytics();

	const projectData = useLazyLoadQuery<JsmCreateSurveyCardQuery>(
		graphql`
			query JsmCreateSurveyCardQuery($cloudId: ID!, $projectIdOrKey: String!) {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					key
				}
			}
		`,
		{ cloudId, projectIdOrKey },
	);

	const projectKey = projectData.jira_projectByIdOrKey?.key ?? projectIdOrKey;

	const {
		isOpen,
		handleToggle,
		isInSmartCreateModal,
		hasCreated,
		hasDismissed,
		isCreating,
		errorKey,
		onCreateSurvey,
		onDismissSurvey,
		areButtonsDisabled,
		actionData,
		openSurveyUrl,
	} = useJsmCreateSurvey({ ...props, projectKey });

	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();

	const errorMessage = getSurveyCreationFlagContent(errorKey, formatMessage).description;

	useEffect(() => {
		if (!errorKey || isInSmartCreateModal) {
			return;
		}
		const flagContent = getSurveyCreationFlagContent(errorKey, formatMessage);
		showFlag({
			id: `conversation-assistant.jsm-create-survey-card.error-flag.${errorKey}`,
			title: flagContent.title,
			description: flagContent.description,
			icon: <StatusErrorIcon color={token('color.icon.danger')} size="small" label="" />,
			isAutoDismiss: false,
		});
	}, [errorKey, formatMessage, showFlag, isInSmartCreateModal]);

	// Analytics-wrapped event handlers
	const handlePreviewClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'jsmSurveyPreviewCard.preview',
			attributes: {
				projectId: projectIdOrKey,
			},
		});

		onPreview(e);
	};

	const handleOpenClick = (): void => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'jsmSurveyPreviewCard.open',
			attributes: {
				projectId: projectIdOrKey,
			},
		});
	};

	if (hasDismissed) {
		return (
			<SystemMessage
				message={formatMessage(surveyPreviewCardDismissedMessage)}
				isSuccessMessage={false}
			/>
		);
	}

	return (
		<Stack space="space.100">
			{!hasCreated && (
				<Text>
					{formatMessage(surveyPreviewCardActionMessage, {
						bold: (chunks) => <Text weight="bold">{chunks}</Text>,
						title: (_) => <Text weight="bold">{actionData.selectedFormQuestions.title}</Text>,
					})}
				</Text>
			)}
			<Stack xcss={styles.container}>
				<Box xcss={styles.header}>
					<Inline alignInline="center" space="space.100" alignBlock="center">
						<Inline alignBlock="center" alignInline="center" xcss={styles.icon}>
							<SurveyIcon label="Survey icon" color={token('color.icon.accent.blue')} />
						</Inline>
						<Heading size="small">
							<FormattedMessage {...surveyPreviewCardTitle} />
						</Heading>
					</Inline>

					<IconButton
						icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
						label={
							isOpen
								? formatMessage(surveyPreviewCardToggleCloseLabel)
								: formatMessage(surveyPreviewCardToggleOpenLabel)
						}
						onClick={handleToggle}
						appearance="subtle"
						spacing="compact"
						testId="conversation-assistant.jsm-create-survey-preview-card.toggle-button"
						isTooltipDisabled={false}
					/>
				</Box>
				{isOpen && (
					<Stack xcss={styles.content}>
						{isInSmartCreateModal ? (
							<PreviewContent {...actionData} />
						) : (
							<>
								<MainContent {...actionData} />
								<Footer>
									{hasCreated ? (
										<LinkButton href={openSurveyUrl} onClick={handleOpenClick}>
											<FormattedMessage {...surveyPreviewCardOpenButton} />
										</LinkButton>
									) : (
										<Inline
											alignBlock="center"
											alignInline="end"
											spread={fg('surveys-aifc-dismiss-button') ? 'space-between' : undefined}
											grow="fill"
										>
											{fg('surveys-aifc-dismiss-button') && (
												<Button
													appearance="subtle"
													onClick={onDismissSurvey}
													isDisabled={areButtonsDisabled}
												>
													<FormattedMessage {...surveyPreviewCardDismissButton} />
												</Button>
											)}
											<Inline space="space.100">
												<Button
													appearance="subtle"
													onClick={handlePreviewClick}
													isDisabled={areButtonsDisabled}
												>
													<FormattedMessage {...surveyPreviewCardPreviewButton} />
												</Button>
												<Button
													appearance="primary"
													onClick={onCreateSurvey}
													isDisabled={areButtonsDisabled}
													iconBefore={isCreating ? () => <Spinner size="small" /> : undefined}
												>
													<FormattedMessage
														{...(isCreating
															? surveyPreviewCardCreatingButton
															: surveyPreviewCardCreateButton)}
													/>
												</Button>
											</Inline>
										</Inline>
									)}
								</Footer>
							</>
						)}
					</Stack>
				)}
			</Stack>
			{hasCreated && !isInSmartCreateModal && (
				<SystemMessage
					message={formatMessage(surveyPreviewCardSuccessMessage, {
						title: actionData.selectedFormQuestions.title,
					})}
				/>
			)}
			{errorKey && <SystemMessage isSuccessMessage={false} message={errorMessage} />}
		</Stack>
	);
};
